import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useSEO } from "../hooks/useSEO";
import * as api from "../services/api";
import { track } from "../services/events";

const categoryDescriptions: Record<string, string> = {
  finance: "Make smarter financial decisions with instant calculations and comparisons.",
  analytics: "Uncover insights from your data with powerful analysis tools.",
  marketing: "Optimize your content and reach more customers online.",
  design: "Create beautiful, cohesive visuals with intelligent design tools.",
  developer: "Debug, format, and validate code faster than ever.",
  productivity: "Save time and work more effectively every day.",
};

const categoryFeatures: Record<string, string[]> = {
  finance: ["No spreadsheets required", "Instant calculations", "Export results as CSV or PDF"],
  analytics: ["Visualize complex data", "Statistical analysis built in", "Share results with your team"],
  marketing: ["Audit your content in seconds", "Keyword and SEO insights", "Actionable recommendations"],
  design: ["Color theory applied automatically", "Typography pairings that work", "Export-ready results"],
  developer: ["Browser-based, nothing to install", "JSON, regex, and code utilities", "Syntax validation included"],
  productivity: ["Automate repetitive tasks", "Works in your browser", "No sign-up required for basic tools"],
};

export default function LandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [tool, setTool] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // UTM tracking from query string
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");
    if (utmSource || utmMedium || utmCampaign) {
      track("landing_page_visit", { slug, utmSource, utmMedium, utmCampaign });
    } else {
      track("landing_page_visit", { slug });
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    api.getTool(slug)
      .then((data) => setTool(data.tool))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const description = tool
    ? `Use ${tool.name} for free on 9nd.org. ${tool.description}`
    : undefined;

  useSEO({
    title: tool ? tool.name + " — Free Online Tool" : undefined,
    description,
    canonical: tool ? `https://9nd.org/lp/${slug}` : undefined,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !tool) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-bold mb-4">Tool Not Found</h1>
        <p className="text-gray-600 mb-8">We couldn't find that tool. Browse all our tools below.</p>
        <Link to="/tools" className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition">Browse Tools</Link>
      </div>
    );
  }

  const catFeatures = categoryFeatures[tool.category] || categoryFeatures.productivity;
  const catDesc = categoryDescriptions[tool.category] || "";

  const categoryBadgeColors: Record<string, string> = {
    finance: "bg-green-100 text-green-800",
    analytics: "bg-blue-100 text-blue-800",
    marketing: "bg-orange-100 text-orange-800",
    design: "bg-purple-100 text-purple-800",
    developer: "bg-gray-100 text-gray-800",
    productivity: "bg-teal-100 text-teal-800",
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": tool.name,
    "description": tool.description,
    "applicationCategory": "WebApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
    },
    "url": `https://9nd.org/tools/${tool.slug}`,
  };

  return (
    <>
      {/* Structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Minimal nav */}
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/assets/brand/logo-mark.png" alt="9nd.org" className="h-7 w-7" />
            <span className="font-bold text-gray-900">9nd.org</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">Sign in</Link>
            <Link to={`/tools/${tool.slug}`} className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition">
              Try for Free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-indigo-900 text-white py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <span className={"inline-block text-xs font-semibold px-3 py-1 rounded-full mb-6 " + (categoryBadgeColors[tool.category] || "bg-gray-100 text-gray-800")}>
            {tool.category.toUpperCase()}
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight">{tool.name}</h1>
          <p className="text-lg sm:text-xl text-indigo-200 mb-8 max-w-xl mx-auto">{tool.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`/tools/${tool.slug}`}
              onClick={() => track("landing_cta_click", { slug: tool.slug, position: "hero" })}
              className="bg-indigo-500 hover:bg-indigo-400 text-white font-bold px-8 py-4 rounded-xl text-lg transition"
            >
              Use {tool.name} Free →
            </Link>
            <Link to="/pricing" className="border border-indigo-400 text-indigo-200 hover:text-white font-semibold px-8 py-4 rounded-xl text-lg transition">
              View Pro Plans
            </Link>
          </div>
        </div>
      </section>

      {/* Category value prop */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-lg text-gray-600 mb-12">{catDesc}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {catFeatures.map((feature) => (
              <div key={feature} className="flex items-start gap-3 p-4 border rounded-xl">
                <span className="text-indigo-500 text-xl mt-0.5">✓</span>
                <p className="text-gray-700 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-10">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { step: "1", label: "Enter your data", desc: "Fill in the simple form — no spreadsheets or software needed." },
              { step: "2", label: "Run the tool", desc: "Results are calculated instantly in your browser." },
              { step: "3", label: "Export & share", desc: "Download as CSV, JSON, or PDF to use anywhere." },
            ].map(({ step, label, desc }) => (
              <div key={step} className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-lg mb-3">{step}</div>
                <h3 className="font-semibold mb-1">{label}</h3>
                <p className="text-sm text-gray-500">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA bottom */}
      <section className="py-20 px-4 bg-indigo-600 text-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Ready to try {tool.name}?</h2>
          <p className="text-indigo-200 mb-8">Free to use. No credit card required. Start in seconds.</p>
          <Link
            to={`/tools/${tool.slug}`}
            onClick={() => track("landing_cta_click", { slug: tool.slug, position: "bottom" })}
            className="bg-white text-indigo-700 font-bold px-10 py-4 rounded-xl text-lg hover:bg-indigo-50 transition"
          >
            Open {tool.name} →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900 text-center text-sm text-gray-500">
        <p>
          <Link to="/" className="hover:text-white transition">9nd.org</Link>
          {" · "}
          <Link to="/tools" className="hover:text-white transition">All Tools</Link>
          {" · "}
          <Link to="/pricing" className="hover:text-white transition">Pricing</Link>
          {" · "}
          <Link to="/about" className="hover:text-white transition">About</Link>
        </p>
        <p className="mt-2">© {new Date().getFullYear()} 9nd.org. All rights reserved.</p>
      </footer>
    </>
  );
}
